import * as THREE from 'three'
import EventEmitter from 'eventemitter3'

import { readPacket, writePacket } from '../shared/packet'

import { ClientInput } from './systems/ClientInput'
import { Stats } from './Stats'
// import { ClientNameplates } from './systems/ClientNameplates'
import { storage } from './utils'
import { ClientCombatText } from './systems/ClientCombatText'
import { TilesetAtlasProvider } from './TilesetAtlasProvider'
import { ImageLoader } from './ImageLoader'
import { Atlas } from './Atlas'

import { Physics } from '../shared/Physics'
import { CTFlags } from '../shared/CTFlags'

import { ClientInventory } from './systems/ClientInventory'
import { ClientChat } from './systems/ClientChat'
import { ClientController } from './systems/ClientController'
import { ClientEntities } from './systems/ClientEntities'
import { ClientChunks } from './systems/ClientChunks'
import { ClientEditor } from './systems/ClientEditor'
import { ClientLights } from './systems/ClientLights'
import { Targets } from '../shared/Targets'
import { ClientTiles } from './systems/ClientTiles'
import { ClientSprites } from './systems/ClientSprites'
import { ClientShadows } from './systems/ClientShadows'
import { PhysicsDebug } from './systems/PhysicsDebug'
import { ClientProjectiles } from './systems/ClientProjectiles'
import { ClientFrames } from './systems/ClientFrames'
import { ClientAudio } from './systems/ClientAudio'
import { preloadAssets } from '../shared/interfaces'
import { FlagOverlay } from './FlagOverlay'

const MAX_DELTA_TIME = 1 / 30
const STATS_RATE = 1 / 10
const pointerWorld = new THREE.Vector3()

class RollingAverage {
  constructor(size) {
    this._buf = new Float64Array(size)
    this._sum = 0
    this._idx = 0
    this._count = 0
  }
  add(value) {
    this._sum -= this._buf[this._idx]
    this._buf[this._idx] = value
    this._sum += value
    this._idx = (this._idx + 1) % this._buf.length
    if (this._count < this._buf.length) this._count++
  }
  get value() {
    return this._count === 0 ? 0 : this._sum / this._count
  }
}

let renderer
const getRenderer = () => {
  if (!renderer) {
    renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false })
  }
  return renderer
}

export class Client extends EventEmitter {
  constructor(viewport) {
    super()
    this.viewport = viewport
    this.width = viewport.offsetWidth
    this.height = viewport.offsetHeight

    this.loader = new ImageLoader()

    // ── renderer ──
    this.renderer = getRenderer()
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(this.width, this.height)
    this.renderer.setClearColor(0x0a0a0a)
    this.renderer.sortObjects = true
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace
    viewport.appendChild(this.renderer.domElement)

    // ── scene & camera ──
    this.scene = new THREE.Scene()
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100)
    this.camera.position.set(0, 0, 50)
    this.camera.lookAt(0, 0, 0)
    this.updateCamera()

    // ── entities ──
    this.entities = new ClientEntities(this)
    this.chunks = new ClientChunks(this)

    // ── shared uniforms ──
    this.uniforms = {
      uTime: { value: 0 },
      uCameraY: { value: 0 },
    }

    // ── subsystems ──
    this.stats = new Stats()
    this.stats.init(this.renderer)

    this.frames = new ClientFrames(this)
    // this.nameplates = new ClientNameplates(this, '/assets/healthbar.png')
    this.input = new ClientInput(this)
    this.tileAtlas = new Atlas(this, { sourceProvider: new TilesetAtlasProvider(this) })
    this.spriteAtlas = new Atlas(this)
    this.inventory = new ClientInventory(this)
    this.chat = new ClientChat(this)
    this.controller = new ClientController(this)
    this.lights = new ClientLights(this)
    this.tiles = new ClientTiles(this)
    this.targets = new Targets()
    this.physics = new Physics()
    this.physicsDebug = new PhysicsDebug(this)
    this.combatText = new ClientCombatText(this)
    this.editor = new ClientEditor(this)
    this.flagOverlay = new FlagOverlay(this.scene)
    this.sprites = new ClientSprites(this) // sprite layer (y-sorted individual meshes)
    this.shadows = new ClientShadows(this) // shadow layer (fixed z, one draw call)
    this.projectiles = new ClientProjectiles(this)
    this.audio = new ClientAudio(this)

    // ── timing ──
    this.time = 0
    this.frame = 0
    this.delta = 0

    this.lastStats = 0
    this._bytesIn = 0
    this._bytesOut = 0
    this._lastPing = 0
    this._pingSentAt = 0
    this._avgPing = new RollingAverage(30)
    this._avgNetIn = new RollingAverage(30)
    this._avgNetOut = new RollingAverage(30)

    // ── hot update functions ──
    this.hot = new Set()

    // ── start loop ──
    this.renderer.setAnimationLoop(this.update)
    window.addEventListener('resize', this.onResize)

    // ── network ──
    let url = globalThis.env.WS_URL
    let authToken = storage.get('authToken')
    if (authToken) url += `?authToken=${authToken}`
    this.ws = new WebSocket(url)
    this.ws.binaryType = 'arraybuffer'
    this.ws.addEventListener('message', this.onMessage)
    this.ws.addEventListener('close', this.onDisconnect)

    // -- temp lighting
    this.lights.setDarkness(0)

    window.client = this
  }

  // ── hot system (per-entity update functions) ──

  setHot(fn, enabled) {
    if (enabled) {
      this.hot.add(fn)
    } else {
      this.hot.delete(fn)
    }
  }

  // ── entity management ──

  getWorldPointerPosition() {
    pointerWorld.set(this.input.pointer.coords.x * 2 - 1, -(this.input.pointer.coords.y * 2 - 1), 0)
    pointerWorld.unproject(this.camera)
    return { x: pointerWorld.x, y: pointerWorld.y }
  }

  getPointerTilePosition() {
    const world = this.getWorldPointerPosition()
    return {
      x: Math.floor(world.x + 0.5),
      y: Math.floor(world.y),
    }
  }

  // ── camera ──

  setCameraTarget(x, y, lerp = 0.3 /* 0.1 */) {
    if (lerp) {
      this.camera.position.x += (x - this.camera.position.x) * lerp
      this.camera.position.y += (y - this.camera.position.y) * lerp
    } else {
      this.camera.position.x = x
      this.camera.position.y = y
    }
    this.uniforms.uCameraY.value = this.camera.position.y
    this.camera.lookAt(this.camera.position.x, this.camera.position.y, 0)
  }

  updateCamera() {
    const aspect = this.width / this.height
    const diagonalMin = 10 // tiles visible corner-to-corner on mobile
    const diagonalMax = 18 // tiles visible corner-to-corner on desktop
    // debug big!
    // const diagonalMin = 100
    // const diagonalMax = 100
    const screenDiagonal = Math.sqrt(this.width * this.width + this.height * this.height)
    const t = Math.min(1, Math.max(0, (screenDiagonal - 600) / 800)) // 600px = small phone, 1400px = desktop
    const diagonal = diagonalMin + (diagonalMax - diagonalMin) * t
    this.viewSize = diagonal / Math.sqrt(1 + aspect * aspect)
    this.camera.left = -this.viewSize * aspect
    this.camera.right = this.viewSize * aspect
    this.camera.top = this.viewSize
    this.camera.bottom = -this.viewSize
    this.camera.updateProjectionMatrix()
  }
  // ── update loop ──

  update = time => {
    this.stats.begin()
    time /= 1000
    let delta = time - this.time
    if (delta < 0) delta = 0
    if (delta > MAX_DELTA_TIME) delta = MAX_DELTA_TIME
    this.time = time
    this.frame++
    this.delta = delta
    this.uniforms.uTime.value += delta

    // input
    this.input.update(delta, time)
    this.editor.update(delta, time)
    this.controller.update(delta, time)

    // entity hot updates
    for (const fn of this.hot) {
      fn(delta, time)
    }

    // subsystem updates
    this.frames.update(delta, time)
    // this.nameplates.update(delta, time)
    this.tiles.update(delta, time)
    this.sprites.update(delta, time)
    this.shadows.update(delta, time)
    this.lights.update(delta, time)
    this.physics.update(delta, time)
    this.physicsDebug.update(delta, time)
    this.combatText.update(delta, time)
    this.projectiles.update(delta, time)

    this.input.lateUpdate(delta, time)

    this.render()
    this.stats.end()
    this.stats.update()

    this.lastStats += delta
    if (this.lastStats > STATS_RATE) {
      const elapsed = this.lastStats
      this.lastStats -= STATS_RATE
      this._avgNetIn.add(this._bytesIn / elapsed)
      this._avgNetOut.add(this._bytesOut / elapsed)
      this._avgPing.add(this._lastPing)
      this._bytesIn = 0
      this._bytesOut = 0
      this.send({ type: 'ping', t: Date.now() })
      this.emit('stats', {
        fps: this.stats.data.fps,
        cpu: this.stats.data.cpu,
        gpu: this.stats.data.gpu,
        draws: this.renderer.info.render.calls,
        pos: this.player
          ? { x: this.player.getPosition().x.toFixed(0), y: this.player.getPosition().y.toFixed(0) }
          : { x: 0, y: 0 },
        ping: this._avgPing.value,
        netIn: this._avgNetIn.value,
        netOut: this._avgNetOut.value,
      })
    }
  }

  render() {
    this.renderer.render(this.scene, this.camera)
  }

  send(msg) {
    if (this.ws.readyState !== WebSocket.OPEN) return
    const packet = writePacket(msg)
    this._bytesOut += packet.byteLength
    this.ws.send(packet)
  }

  onMessage = e => {
    this._bytesIn += e.data.byteLength
    const msg = readPacket(e.data)
    const { type, ...data } = msg
    const method = `on${type[0].toUpperCase()}${type.slice(1)}`
    this[method]?.(data)
  }

  onPong = ({ t }) => {
    this._lastPing = Date.now() - t
  }

  onDisconnect = () => {
    this.destroy()
    this.emit('disconnect')
  }

  onResize = () => {
    this.width = this.viewport.offsetWidth
    this.height = this.viewport.offsetHeight
    this.renderer.setSize(this.width, this.height)
    this.updateCamera()
  }

  destroy() {
    this.renderer.setAnimationLoop(null)
    window.removeEventListener('resize', this.onResize)
  }

  // -- network handlers --

  onLoadChunk({ chunk }) {
    this.chunks.add(chunk)
    const c = this.chunks.get(chunk.id)
    if (c) this.flagOverlay.update(c)
  }

  onUnloadChunk({ chunkId }) {
    this.chunks.remove(chunkId)
    this.flagOverlay.remove(chunkId)
  }

  onSetChunkTiles({ changes }) {
    this.chunks.applyTileChanges(changes)
  }

  onSetChunkFlags({ changes }) {
    this.chunks.applyFlagChanges(changes)
  }

  onInit({ playerId, inventory, authToken, chat }) {
    // console.log('inventory', inventory)
    this.playerId = playerId
    this.inventory.setInventory(inventory)
    this.chat.init(chat)
    if (authToken) {
      storage.set('authToken', authToken)
    }
    preloadAssets(this.loader)
  }

  onAddEntities({ entities }) {
    this.entities.addMany(entities)
  }

  onAddEntity({ entity }) {
    this.entities.add(entity)
  }

  onRemoveEntities({ chunkId }) {
    this.entities.removeByChunk(chunkId)
  }

  onRemoveEntity({ entityId }) {
    this.entities.remove(entityId)
  }

  onMoveEntity({ id, x, y, d, l }) {
    const entity = this.entities.get(id)
    if (!entity) return
    entity.networkMove(x, y)
    if (d) entity.face?.(d)
    if (l) entity.setLocomotion?.(l)
  }

  onMoveEntities({ moves }) {
    for (const { id, x, y, d, l } of moves) {
      if (id === this.playerId) continue
      const entity = this.entities.get(id)
      if (!entity) continue
      entity.networkMove(x, y)
      if (d) entity.face?.(d)
      if (l) entity.setLocomotion?.(l)
    }
  }

  onTeleportEntity({ id, x, y }) {
    this.entities.get(id)?.teleport(x, y, true)
  }

  onSet({ id, values }) {
    this.entities.get(id)?.set(values)
  }

  onInterface({ interface: currentInterface }) {
    this.inventory.setInterface(currentInterface)
  }

  onInventory({ inventory, gained }) {
    this.inventory.setInventory(inventory, gained)
  }

  onChat({ msg }) {
    this.chat.receive(msg)
    this.entities.get(msg.entityId)?.showChatBubble?.(msg.text)
  }

  onProjectile(data) {
    const shooter = data.shooterId ? this.entities.get(data.shooterId) : null
    this.projectiles.spawn({ ...data, shooter })
  }

  onProjectileStop({ projId, x, y }) {
    this.projectiles.stop(projId, x, y)
  }

  onEmote({ entityId, emote }) {
    this.entities.get(entityId)?.playEmote?.(emote)
  }

  onWobble({ entityId }) {
    this.entities.get(entityId)?.wobble?.()
  }

  onActivityReject({ entityId }) {
    this.controller.onActivityReject(entityId)
  }

  onActivityEnd({ entityId }) {
    this.controller.onActivityEnd(entityId)
  }

  onSound({ entityId, sound }) {
    const entity = this.entities.get(entityId)
    if (!entity) return
    const pos = entity.getPosition()
    this.audio.play(sound, pos.x, pos.y)
  }

  onCt({ entityId, text, flags }) {
    const entity = this.entities.get(entityId)
    if (!entity) return
    const opts = {
      x: entity.getBase().x,
      y: entity.getBase().y + 1.3,
      text,
    }
    if (flags & CTFlags.DAMAGE) {
      opts.color = '#fff'
      opts.style = 'arc'
      if (entityId === this.playerId) {
        const pos = entity.getPosition()
        this.audio.play({ name: 'hurt-oof', pitch: 0.1 }, pos.x, pos.y)
      }
    } else if (flags & CTFlags.DAMAGE_CRIT) {
      opts.color = '#ff3d3d'
      opts.style = 'arc'
      opts.scale = 2
    } else if (flags & CTFlags.HEAL) {
      opts.color = '#3bf74e'
      opts.style = 'rise'
    } else if (flags & CTFlags.HEAL_CRIT) {
      opts.color = '#3bf74e'
      opts.style = 'rise'
      opts.scale = 2
    } else if (flags & CTFlags.INFO) {
      opts.color = '#ff5757ff'
      opts.style = 'rise'
    }
    this.combatText.show(opts)
  }
}
